// line-selection.ts — Selection actions of the input table (book 4.8): duplicate and delete (multi).
//
// Pure state manipulation on `state.lines`, never calling compute(): recomputations follow from the normal
// recompute. Handles linked sets (parent + child with reciprocal `linkId`) and persistent, never-reused `num`.

import type { QuoteLine } from "../core/core";
import { isCustomSizeSurcharge } from "../products/custom-size-line";
import { applyLinkRefs, isTriggerChild, parentHasTriggerFor } from "../products/triggers";

/**
 * Reciprocal partner of a line (parent/child), or undefined. A non-reciprocal `linkId` is ignored.
 * @param l Line whose partner is looked up
 * @param byId Lines indexed by id
 * @returns The partner line, or undefined when there is none (or the link is not reciprocal)
 */
function linkedPartner(l: QuoteLine, byId: Map<number, QuoteLine>): QuoteLine | undefined {
  if (l.id == null || l.linkId == null) return undefined;
  const p = byId.get(l.linkId);
  return p && p.linkId === l.id ? p : undefined;
}

/**
 * Extends a set of selected ids with the trigger-created children of the checked parents (§7), so the
 * displayed count matches the cascade. Manually linked lines are left out. A custom-size surcharge
 * is inseparable from its parent: selecting the child also selects the parent.
 * @param lines Quote lines
 * @param ids Ids selected by the user
 * @returns The selected ids plus the ids pulled in by the link rules
 */
export function idsWithLinked(lines: readonly QuoteLine[], ids: readonly number[]): number[] {
  const byId = new Map(lines.filter((l) => l.id != null).map((l) => [l.id!, l] as const));
  const pos = new Map(lines.map((l, i) => [l.id, i] as const));
  const out = new Set(ids);
  for (const id of ids) {
    const l = byId.get(id);
    if (!l) continue;
    const p = linkedPartner(l, byId);
    // l is the parent (partner sits after it) and the child was trigger-created: pull it in.
    if (p && (pos.get(l.id!) ?? 0) < (pos.get(p.id!) ?? 0) && isTriggerChild(p)) out.add(p.id!);
    // Custom-size extra never exists alone — selecting it pulls its parent.
    if (isCustomSizeSurcharge(l) && p) out.add(p.id!);
  }
  return [...out];
}

/**
 * Deletes the lines in `ids` with a generic cascade (§7): any trigger-created child whose parent goes away
 * is removed in the same operation, so no orphan remains. The rule keys on the link plus the origin
 * (`isTriggerChild`), not on the type: a manually linked line survives, simply unlinked.
 * @param lines Quote lines
 * @param ids Ids of the lines to delete
 * @returns The remaining lines, survivors unlinked from deleted partners and link references refreshed
 */
export function removeLines(lines: readonly QuoteLine[], ids: readonly number[]): QuoteLine[] {
  const toDelete = new Set(ids);
  // Cascade: the child points at its parent via linkId, so a deleted parent deletes its trigger child.
  for (const l of lines) {
    if (l.id == null || toDelete.has(l.id)) continue;
    if (isTriggerChild(l) && l.linkId != null && toDelete.has(l.linkId)) toDelete.add(l.id);
  }
  const remaining = lines.filter((l) => l.id == null || !toDelete.has(l.id));
  // Unlink the survivors (manually linked) whose partner is gone.
  const survivorIds = new Set(remaining.map((l) => l.id));
  const unlinked = remaining.map((l) => (l.linkId != null && !survivorIds.has(l.linkId) ? { ...l, linkId: null, linkLabel: null } : l));
  return applyLinkRefs(unlinked);
}

/**
 * §7 symmetry: after editing a parent's composition, drops the auto-created children whose trigger is gone
 * (hour-based cut unchecked: the service line goes; finishing removed from every column: the per-metre line
 * goes) — same effect as deleting the parent, without deleting it.
 * Idempotent and cheap — with nothing stale the input array is returned as-is (no re-render).
 * @param lines Quote lines
 * @returns The lines without the obsolete trigger children (the same array when nothing is obsolete)
 */
export function removeObsoleteTriggerChildren(lines: readonly QuoteLine[]): QuoteLine[] {
  const byId = new Map(lines.filter((l) => l.id != null).map((l) => [l.id!, l] as const));
  const obsolete = new Set<number>();
  for (const l of lines) {
    if (l.id == null || !isTriggerChild(l) || l.linkId == null) continue;
    const parent = byId.get(l.linkId);
    if (parent && !parentHasTriggerFor(l, parent)) obsolete.add(l.id);
  }
  if (!obsolete.size) return lines as QuoteLine[];
  return removeLines(lines, [...obsolete]);
}

/**
 * Duplicates the selected lines. Each copy is inserted right below its source (order preserved), gets a
 * fresh id (`nextId`) and a fresh persistent number (`nextNum`, never reused), and copies all content.
 * Linked sets:
 * - parent checked: the whole block is duplicated and links are recreated between the copies;
 * - child checked alone: copied without a link (except a custom-size surcharge, inseparable from its parent);
 * - parent + child checked: a single block duplication, no duplicate.
 * Pure: no effect on the calculation (tiers and purchase prices follow from the recompute).
 * @param lines Quote lines
 * @param ids Ids of the lines to duplicate
 * @param nextId Generator of fresh line ids
 * @param nextNum Generator of fresh persistent line numbers
 * @returns The lines with the copies inserted and link references refreshed
 */
export function duplicateLines(lines: readonly QuoteLine[], ids: readonly number[], nextId: () => number, nextNum: () => number): QuoteLine[] {
  const selectedIds = new Set(ids);
  const byId = new Map(lines.filter((l) => l.id != null).map((l) => [l.id!, l] as const));
  const pos = new Map(lines.map((l, i) => [l.id, i] as const));
  /**
   * Is `l` the parent of `p` (i.e. does it sit before its partner)?
   * @param l Candidate parent
   * @param p Its partner
   * @returns True when `l` precedes `p` in the quote
   */
  const isParent = (l: QuoteLine, p: QuoteLine): boolean => (pos.get(l.id!) ?? 0) < (pos.get(p.id!) ?? 0);

  // Deduplicated duplication units: {parent, child} block or a single line.
  type DuplicationUnit = { type: "block"; parent: QuoteLine; child: QuoteLine } | { type: "single"; line: QuoteLine };
  const units: DuplicationUnit[] = [];
  const handled = new Set<number>();
  for (const l of lines) {
    if (l.id == null || !selectedIds.has(l.id) || handled.has(l.id)) continue;
    const p = linkedPartner(l, byId);
    if (p) {
      const parent = isParent(l, p) ? l : p;
      const child = parent === l ? p : l;
      if (parent === l || selectedIds.has(parent.id!) || isCustomSizeSurcharge(child)) {
        // Parent checked, or custom-size child (inseparable from parent): duplicate as a block.
        units.push({ type: "block", parent, child });
        handled.add(parent.id!); handled.add(child.id!);
      } else {
        units.push({ type: "single", line: l }); // lone (non custom-size) child: copy without a link
        handled.add(l.id);
      }
    } else {
      units.push({ type: "single", line: l });
      handled.add(l.id);
    }
  }

  // Copies indexed by the position after which to insert them (end of source block / source line).
  const insertAfter = new Map<number, QuoteLine[]>();
  /**
   * Registers copies to insert after a given position.
   * @param insertPos Index of the line after which the copies go
   * @param copies Copies to insert, in order
   * @returns The updated insertion map
   */
  const pushCopies = (insertPos: number, ...copies: QuoteLine[]): Map<number, QuoteLine[]> => insertAfter.set(insertPos, [...(insertAfter.get(insertPos) ?? []), ...copies]);
  for (const u of units) {
    if (u.type === "block") {
      const parentId = nextId(), childId = nextId();
      const parentCopy: QuoteLine = { ...u.parent, id: parentId, num: nextNum(), linkId: childId, linkLabel: u.parent.linkLabel };
      const childCopy: QuoteLine = { ...u.child, id: childId, linkId: parentId, linkLabel: u.child.linkLabel }; // child: no number of its own (renders "N-1")
      pushCopies(pos.get(u.child.id!)!, parentCopy, childCopy); // after the child = after the block
    } else {
      pushCopies(pos.get(u.line.id!)!, { ...u.line, id: nextId(), num: nextNum(), linkId: null, linkLabel: null });
    }
  }

  const out: QuoteLine[] = [];
  lines.forEach((l, i) => {
    out.push(l);
    const inserted = insertAfter.get(i);
    if (inserted) out.push(...inserted);
  });
  return applyLinkRefs(out);
}
